import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createSegment, type Job, type Line, type Segment } from '@otto/rebar-engine';
import { LineForm } from './LineForm';
import {
  buildTree,
  cloneSegs,
  computeLinealFeet,
  emptyLineForm,
  flattenRows,
  getSiblings,
  lineRowId,
  lineToForm,
  newSegmentId,
  parseLinesFromText,
  segmentRowId,
  type FlatRow,
} from './helpers';
import {
  COLORS,
  chevronBtn,
  dangerBtnSmall,
  ghostBtn,
  iconBtn,
  iconBtnDanger,
  mono,
  primaryBtn,
  segmentNameInput,
  tagBtn,
} from './styles';

const emptyCard: React.CSSProperties = {
  padding: 14,
  background: COLORS.bg,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 8,
};

interface Props {
  job: Job;
  onChange: (segments: Segment[]) => void;
}

export function SegmentTree({ job, onChange }: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(job.segments.map((s) => s.segmentId)));
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [lineEditor, setLineEditor] = useState<{ segmentId: string; lineNumber: number | null } | null>(null);
  const [focusedRowId, setFocusedRowId] = useState<string | null>(null);
  const rowRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());

  const tree = useMemo(() => buildTree(job.segments), [job.segments]);
  const flatRows = useMemo(() => flattenRows(job.segments, expanded), [job.segments, expanded]);

  // Drop focus if focused row disappears
  useEffect(() => {
    if (focusedRowId && !flatRows.some((r) => r.id === focusedRowId)) {
      setFocusedRowId(null);
    }
  }, [flatRows, focusedRowId]);

  // When focused row changes, move DOM focus to its container
  useEffect(() => {
    if (focusedRowId && lineEditor === null && renamingId === null) {
      rowRefs.current.get(focusedRowId)?.focus();
    }
  }, [focusedRowId, lineEditor, renamingId]);

  const registerRowRef = useCallback(
    (id: string) => (el: HTMLDivElement | null) => {
      if (el === null) rowRefs.current.delete(id);
      else rowRefs.current.set(id, el);
    },
    [],
  );

  // --- Mutations ---
  // Note: weight rollup is owned by the route's useMemo over `job` so the live
  // weight summary stays authoritative. Calling rollupSegments here with an
  // empty barMarks map would zero out totalWeight on every commit.
  function commitSegments(next: Segment[]) {
    onChange(next);
  }

  function toggleSegmentField(segmentId: string, field: 'alternateBid' | 'costingIncluded') {
    commitSegments(
      cloneSegs(job.segments).map((s) =>
        s.segmentId === segmentId ? { ...s, [field]: !s[field] } : s,
      ),
    );
  }

  function addSegment(parentSegmentId: string | null) {
    const siblings = job.segments.filter((s) => s.parentSegmentId === parentSegmentId);
    const seg = createSegment({
      segmentId: newSegmentId(),
      name: 'New Segment',
      parentSegmentId,
      sortOrder: siblings.length,
    });
    commitSegments([...cloneSegs(job.segments), seg]);
    setRenamingId(seg.segmentId);
    setRenameValue(seg.name);
    if (parentSegmentId) setExpanded((e) => new Set([...e, parentSegmentId]));
    setExpanded((e) => new Set([...e, seg.segmentId]));
  }

  function deleteSegment(segmentId: string) {
    const seg = job.segments.find((s) => s.segmentId === segmentId);
    if (!seg) return;
    const hasLines = seg.lines.length > 0;
    const hasChildren = job.segments.some((s) => s.parentSegmentId === segmentId);
    if ((hasLines || hasChildren) && !window.confirm(`Delete "${seg.name}" and all its contents?`)) return;

    const toDelete = new Set<string>();
    function collect(id: string) {
      toDelete.add(id);
      for (const s of job.segments) if (s.parentSegmentId === id) collect(s.segmentId);
    }
    collect(segmentId);
    commitSegments(cloneSegs(job.segments).filter((s) => !toDelete.has(s.segmentId)));
  }

  function commitRename(segmentId: string) {
    const name = renameValue.trim() || 'Unnamed';
    commitSegments(
      cloneSegs(job.segments).map((s) => (s.segmentId === segmentId ? { ...s, name } : s)),
    );
    setRenamingId(null);
  }

  function moveSegment(segmentId: string, direction: 'up' | 'down') {
    const seg = job.segments.find((s) => s.segmentId === segmentId);
    if (!seg) return;
    const siblings = getSiblings(job.segments, seg);
    const idx = siblings.findIndex((s) => s.segmentId === segmentId);
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= siblings.length) return;
    const other = siblings[targetIdx];
    commitSegments(
      cloneSegs(job.segments).map((s) => {
        if (s.segmentId === segmentId) return { ...s, sortOrder: other.sortOrder };
        if (s.segmentId === other.segmentId) return { ...s, sortOrder: seg.sortOrder };
        return s;
      }),
    );
  }

  function saveLine(segmentId: string, line: Line, advance: boolean) {
    const next = cloneSegs(job.segments).map((s) => {
      if (s.segmentId !== segmentId) return s;
      const idx = s.lines.findIndex((l) => l.lineNumber === line.lineNumber);
      const lines =
        idx >= 0 ? s.lines.map((l, i) => (i === idx ? line : l)) : [...s.lines, line];
      return { ...s, lines };
    });
    commitSegments(next);

    if (advance) {
      const seg = next.find((s) => s.segmentId === segmentId);
      const nextLine = seg?.lines
        .filter((l) => l.lineNumber > line.lineNumber)
        .sort((a, b) => a.lineNumber - b.lineNumber)[0];
      if (nextLine) {
        setLineEditor({ segmentId, lineNumber: nextLine.lineNumber });
        setFocusedRowId(lineRowId(segmentId, nextLine.lineNumber));
        return;
      }
      // No next existing line — open a new-line form
      setLineEditor({ segmentId, lineNumber: null });
      return;
    }
    setLineEditor(null);
    const rowId = lineRowId(segmentId, line.lineNumber);
    setFocusedRowId(rowId);
    queueMicrotask(() => rowRefs.current.get(rowId)?.focus());
  }

  function moveLine(segmentId: string, lineNumber: number, direction: 'up' | 'down') {
    const seg = job.segments.find((s) => s.segmentId === segmentId);
    if (!seg) return;
    const sorted = [...seg.lines].sort((a, b) => a.lineNumber - b.lineNumber);
    const idx = sorted.findIndex((l) => l.lineNumber === lineNumber);
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (idx < 0 || targetIdx < 0 || targetIdx >= sorted.length) return;
    const a = sorted[idx];
    const b = sorted[targetIdx];
    const next = cloneSegs(job.segments).map((s) => {
      if (s.segmentId !== segmentId) return s;
      const lines = s.lines.map((l) => {
        if (l.lineNumber === a.lineNumber) return { ...l, lineNumber: b.lineNumber };
        if (l.lineNumber === b.lineNumber) return { ...l, lineNumber: a.lineNumber };
        return l;
      });
      return { ...s, lines };
    });
    commitSegments(next);
    setFocusedRowId(lineRowId(segmentId, b.lineNumber));
  }

  function deleteLine(segmentId: string, lineNumber: number) {
    commitSegments(
      cloneSegs(job.segments).map((s) =>
        s.segmentId === segmentId
          ? { ...s, lines: s.lines.filter((l) => l.lineNumber !== lineNumber) }
          : s,
      ),
    );
  }

  function toggleExpand(segmentId: string) {
    setExpanded((e) => {
      const n = new Set(e);
      if (n.has(segmentId)) n.delete(segmentId);
      else n.add(segmentId);
      return n;
    });
  }

  function openLineEditorForRow(segmentId: string, lineNumber: number | null) {
    setLineEditor({ segmentId, lineNumber });
    if (!expanded.has(segmentId)) setExpanded((e) => new Set([...e, segmentId]));
  }

  function handleSegmentPaste(segmentId: string, e: React.ClipboardEvent<HTMLDivElement>) {
    const text = e.clipboardData.getData('text/plain');
    if (!text || !text.includes('\t')) return;
    e.preventDefault();
    const seg = job.segments.find((s) => s.segmentId === segmentId);
    if (!seg) return;
    const existing = new Set(seg.lines.map((l) => l.lineNumber));
    const { lines: newLines, errors } = parseLinesFromText(text, segmentId, existing);
    if (newLines.length === 0) {
      if (errors.length) alert(`Paste failed:\n${errors.slice(0, 8).join('\n')}`);
      return;
    }
    const next = cloneSegs(job.segments).map((s) =>
      s.segmentId === segmentId ? { ...s, lines: [...s.lines, ...newLines] } : s,
    );
    commitSegments(next);
    if (!expanded.has(segmentId)) setExpanded((e2) => new Set([...e2, segmentId]));
    setFocusedRowId(lineRowId(segmentId, newLines[0].lineNumber));
    if (errors.length) {
      alert(
        `Pasted ${newLines.length} of ${newLines.length + errors.length} rows. ${errors.length} skipped:\n${errors.slice(0, 8).join('\n')}`,
      );
    }
  }

  function handleRowKeyDown(row: FlatRow, e: React.KeyboardEvent<HTMLDivElement>) {
    const idx = flatRows.findIndex((r) => r.id === row.id);
    if (idx < 0) return;
    const isMod = e.ctrlKey || e.metaKey;

    if (isMod && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault();
      const dir = e.key === 'ArrowUp' ? 'up' : 'down';
      if (row.kind === 'segment') moveSegment(row.segmentId, dir);
      else moveLine(row.segmentId, row.lineNumber, dir);
      return;
    }
    if (isMod) return;

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const next = flatRows[Math.min(idx + 1, flatRows.length - 1)];
        if (next) setFocusedRowId(next.id);
        return;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prev = flatRows[Math.max(idx - 1, 0)];
        if (prev) setFocusedRowId(prev.id);
        return;
      }
      case 'ArrowRight':
        if (row.kind === 'segment') {
          e.preventDefault();
          if (!expanded.has(row.segmentId)) toggleExpand(row.segmentId);
        }
        return;
      case 'ArrowLeft':
        if (row.kind === 'segment') {
          e.preventDefault();
          if (expanded.has(row.segmentId)) toggleExpand(row.segmentId);
        }
        return;
      case 'Enter': {
        e.preventDefault();
        if (row.kind === 'segment') {
          const seg = job.segments.find((s) => s.segmentId === row.segmentId);
          if (seg) {
            setRenamingId(seg.segmentId);
            setRenameValue(seg.name);
          }
        } else {
          openLineEditorForRow(row.segmentId, row.lineNumber);
        }
        return;
      }
      case 'n':
      case 'N':
        e.preventDefault();
        openLineEditorForRow(row.segmentId, null);
        return;
      case 'Delete':
        e.preventDefault();
        if (row.kind === 'segment') deleteSegment(row.segmentId);
        else if (window.confirm(`Delete line #${row.lineNumber}?`)) deleteLine(row.segmentId, row.lineNumber);
        return;
    }
  }

  // --- Render ---

  if (job.segments.length === 0) {
    return (
      <div style={emptyCard}>
        <div style={{ ...mono(12, COLORS.textDim), marginBottom: 10 }}>NO SEGMENTS YET</div>
        <p style={{ fontSize: 13, color: COLORS.textMuted, margin: '0 0 12px' }}>
          A segment groups rebar lines for a single structural element (e.g. footing, column, beam). Add one to start.
        </p>
        <button type="button" onClick={() => addSegment(null)} style={primaryBtn}>
          + Add segment
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        background: COLORS.bg,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 14px',
          borderBottom: `1px solid ${COLORS.border}`,
        }}
      >
        <span style={{ ...mono(10, COLORS.textDim), letterSpacing: '0.1em', flex: 1 }}>SEGMENT TREE</span>
        <span style={mono(10, COLORS.textDim)}>↑↓ Enter N Del · Cmd+↑↓ reorder · paste TSV</span>
        <button type="button" onClick={() => addSegment(null)} style={ghostBtn}>
          + Add segment
        </button>
      </div>

      {tree.map(({ seg, depth }) => {
        const isExpanded = expanded.has(seg.segmentId);
        const isRenaming = renamingId === seg.segmentId;
        const siblings = getSiblings(job.segments, seg);
        const idx = siblings.findIndex((s) => s.segmentId === seg.segmentId);
        const isThisLineEditor = lineEditor?.segmentId === seg.segmentId;
        const nextLineNumber = seg.lines.reduce((mx, l) => Math.max(mx, l.lineNumber), 0) + 1;
        const childCount = job.segments.filter((s) => s.parentSegmentId === seg.segmentId).length;
        const segRowKey = segmentRowId(seg.segmentId);
        const isSegFocused = focusedRowId === segRowKey;

        return (
          <div key={seg.segmentId} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
            <div
              ref={registerRowRef(segRowKey)}
              tabIndex={0}
              onClick={() => setFocusedRowId(segRowKey)}
              onFocus={() => setFocusedRowId(segRowKey)}
              onPaste={(e) => handleSegmentPaste(seg.segmentId, e)}
              onKeyDown={(e) =>
                handleRowKeyDown(
                  { kind: 'segment', id: segRowKey, segmentId: seg.segmentId, depth },
                  e,
                )
              }
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px 8px 0',
                paddingLeft: depth * 18 + 12,
                background: isSegFocused ? COLORS.rowFocus : 'transparent',
                outline: isSegFocused ? `1px solid ${COLORS.rowFocusBorder}` : 'none',
                outlineOffset: -1,
              }}
            >
              <button
                type="button"
                style={chevronBtn}
                onClick={() => toggleExpand(seg.segmentId)}
                aria-label={isExpanded ? `Collapse ${seg.name}` : `Expand ${seg.name}`}
                aria-expanded={isExpanded}
              >
                {isExpanded ? '▾' : '▸'}
              </button>

              {isRenaming ? (
                <input
                  autoFocus
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onBlur={() => commitRename(seg.segmentId)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitRename(seg.segmentId);
                    if (e.key === 'Escape') setRenamingId(null);
                  }}
                  style={segmentNameInput}
                />
              ) : (
                <span
                  onClick={() => {
                    setRenamingId(seg.segmentId);
                    setRenameValue(seg.name);
                  }}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontFamily: "'Syne', sans-serif",
                    fontWeight: 700,
                    fontSize: 14,
                    color: COLORS.text,
                    cursor: 'text',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title="Click to rename"
                >
                  {seg.name}
                </span>
              )}

              <span style={{ ...mono(10, COLORS.textDim), flexShrink: 0 }}>
                {childCount > 0 && `${childCount} seg${childCount !== 1 ? 's' : ''} · `}
                {seg.lines.length} line{seg.lines.length !== 1 ? 's' : ''}
              </span>
              <span style={{ ...mono(11, COLORS.textMuted), width: 90, textAlign: 'right', flexShrink: 0 }}>
                {seg.totalWeight > 0 ? `${Math.round(seg.totalWeight).toLocaleString()} lb` : '—'}
              </span>

              <button
                type="button"
                onClick={() => toggleSegmentField(seg.segmentId, 'alternateBid')}
                style={tagBtn(seg.alternateBid, 'amber')}
                title={seg.alternateBid ? 'Alternate bid — click to unmark' : 'Click to mark as alternate bid'}
              >
                Alt
              </button>
              <button
                type="button"
                onClick={() => toggleSegmentField(seg.segmentId, 'costingIncluded')}
                style={tagBtn(!seg.costingIncluded, 'red')}
                title={seg.costingIncluded ? 'Costing included — click to exclude' : 'Costing excluded — click to include'}
              >
                {seg.costingIncluded ? 'Cost' : 'No Cost'}
              </button>

              <div style={{ display: 'flex', gap: 0, flexShrink: 0 }}>
                <button
                  type="button"
                  onClick={() => moveSegment(seg.segmentId, 'up')}
                  disabled={idx === 0}
                  style={{ ...iconBtn, opacity: idx === 0 ? 0.3 : 1 }}
                  title="Move up"
                  aria-label={`Move ${seg.name} up`}
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => moveSegment(seg.segmentId, 'down')}
                  disabled={idx === siblings.length - 1}
                  style={{ ...iconBtn, opacity: idx === siblings.length - 1 ? 0.3 : 1 }}
                  title="Move down"
                  aria-label={`Move ${seg.name} down`}
                >
                  ↓
                </button>
                <button
                  type="button"
                  onClick={() => addSegment(seg.segmentId)}
                  style={iconBtn}
                  title="Add child segment"
                  aria-label={`Add child segment under ${seg.name}`}
                >
                  +
                </button>
                <button
                  type="button"
                  onClick={() => deleteSegment(seg.segmentId)}
                  style={iconBtnDanger}
                  title="Delete segment"
                  aria-label={`Delete segment ${seg.name}`}
                >
                  ×
                </button>
              </div>
            </div>

            {isExpanded && (
              <div style={{ paddingLeft: depth * 18 + 28, paddingBottom: 4, background: 'rgba(255,255,255,0.015)' }}>
                {seg.lines.length === 0 && !isThisLineEditor && (
                  <p style={{ ...mono(11, COLORS.textDim), padding: '8px 12px', margin: 0 }}>No lines yet.</p>
                )}

                {seg.lines
                  .slice()
                  .sort((a, b) => a.lineNumber - b.lineNumber)
                  .map((line) => {
                    const isEditingThis = isThisLineEditor && lineEditor!.lineNumber === line.lineNumber;
                    const lineKey = lineRowId(seg.segmentId, line.lineNumber);
                    const isLineFocused = focusedRowId === lineKey;
                    return (
                      <div key={line.lineNumber}>
                        <div
                          ref={registerRowRef(lineKey)}
                          tabIndex={0}
                          onClick={() => setFocusedRowId(lineKey)}
                          onFocus={() => setFocusedRowId(lineKey)}
                          onKeyDown={(e) =>
                            handleRowKeyDown(
                              {
                                kind: 'line',
                                id: lineKey,
                                segmentId: seg.segmentId,
                                lineNumber: line.lineNumber,
                                depth: depth + 1,
                              },
                              e,
                            )
                          }
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            padding: '4px 12px',
                            fontSize: 12,
                            color: COLORS.textMuted,
                            background: isLineFocused ? COLORS.rowFocus : 'transparent',
                            outline: isLineFocused ? `1px solid ${COLORS.rowFocusBorder}` : 'none',
                            outlineOffset: -1,
                            borderBottom: `1px dashed ${COLORS.border}`,
                          }}
                        >
                          <span style={{ ...mono(11, COLORS.textDim), width: 32, flexShrink: 0 }}>
                            #{line.lineNumber}
                          </span>
                          <span style={{ ...mono(11, COLORS.textMuted), width: 36, flexShrink: 0 }}>
                            {line.function}
                          </span>
                          <span style={{ ...mono(11, COLORS.textDim), width: 28, flexShrink: 0 }}>
                            {line.material}
                          </span>
                          <span style={{ ...mono(11, COLORS.text), width: 36, flexShrink: 0 }}>
                            {line.size ? `#${line.size.imperial}` : '—'}
                          </span>
                          <span style={{ ...mono(11, COLORS.textMuted), width: 80, flexShrink: 0 }}>
                            {line.L1?.toString() ?? '—'}
                          </span>
                          <span style={{ ...mono(11, COLORS.textMuted), width: 60, flexShrink: 0 }}>
                            {line.quantity} × {line.multiplier}
                          </span>
                          <span style={{ ...mono(11, COLORS.textMuted), width: 70, textAlign: 'right', flexShrink: 0 }}>
                            {(() => {
                              const lf = computeLinealFeet(line);
                              return lf !== null ? `${lf.toFixed(1)} lf` : '—';
                            })()}
                          </span>
                          <span
                            style={{
                              flex: 1,
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: COLORS.textDim,
                              fontSize: 11,
                            }}
                          >
                            {line.notes}
                          </span>
                          <span style={{ ...mono(11, COLORS.text), width: 80, textAlign: 'right', flexShrink: 0 }}>
                            {line.weightLbs > 0 ? `${Math.round(line.weightLbs).toLocaleString()} lb` : '—'}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              setLineEditor({ segmentId: seg.segmentId, lineNumber: line.lineNumber })
                            }
                            style={iconBtn}
                            title="Edit"
                            aria-label={`Edit line ${line.lineNumber}`}
                          >
                            ✎
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteLine(seg.segmentId, line.lineNumber)}
                            style={dangerBtnSmall}
                            title="Delete line"
                            aria-label={`Delete line ${line.lineNumber}`}
                          >
                            ×
                          </button>
                        </div>
                        {isEditingThis && (
                          <LineForm
                            segmentId={seg.segmentId}
                            initial={lineToForm(line)}
                            onSave={(l, advance) => saveLine(seg.segmentId, l, advance)}
                            onCancel={() => setLineEditor(null)}
                          />
                        )}
                      </div>
                    );
                  })}

                {isThisLineEditor && lineEditor!.lineNumber === null ? (
                  <LineForm
                    segmentId={seg.segmentId}
                    initial={emptyLineForm(nextLineNumber)}
                    onSave={(l, advance) => saveLine(seg.segmentId, l, advance)}
                    onCancel={() => setLineEditor(null)}
                  />
                ) : (
                  <div style={{ padding: '6px 12px' }}>
                    <button
                      type="button"
                      onClick={() => openLineEditorForRow(seg.segmentId, null)}
                      style={{
                        ...mono(11, COLORS.textDim),
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    >
                      + Add line
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
